import React, { useState, useMemo, useRef } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DetectedData, ChartType } from '../../utils/dataDetector';
import ChartControls from './ChartControls';
import { csvCell, csvNumberCell } from '../../utils/csvCell';

const COLORS = [
  '#4965ff', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

interface ChatDataChartProps {
  data: DetectedData;
  compact?: boolean;
}

const ChatDataChart: React.FC<ChatDataChartProps> = ({ data, compact = false }) => {
  const [chartType, setChartType] = useState<ChartType>(data.suggestedChartType);
  const [showLegend, setShowLegend] = useState(true);
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Transform data for Recharts format
  const chartData = useMemo(() => {
    return data.labels.map((label, i) => {
      const entry: Record<string, any> = { name: label };
      data.datasets.forEach(ds => {
        entry[ds.label] = ds.values[i];
      });
      return entry;
    });
  }, [data]);

  // Filter datasets if a specific one is selected
  const visibleDatasets = useMemo(() => {
    if (selectedDatasetIndex !== null) {
      return [data.datasets[selectedDatasetIndex]];
    }
    return data.datasets;
  }, [data.datasets, selectedDatasetIndex]);

  const handleExportPng = () => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = svg.clientWidth * 2;
    canvas.height = svg.clientHeight * 2;

    img.onload = () => {
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `chart_${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleExportCsv = () => {
    const headers = ['Label', ...data.datasets.map(ds => csvCell(ds.label))];
    const rows = data.labels.map((label, i) => {
      return [csvCell(label), ...data.datasets.map(ds => csvNumberCell(ds.values[i]))].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chart_data_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const chartHeight = compact ? 250 : 350;

  const renderChart = () => {
    switch (chartType) {
      case 'pie':
        return renderPieChart();
      case 'line':
        return renderLineChart();
      case 'stacked_bar':
        return renderBarChart(true);
      case 'bar':
      default:
        return renderBarChart(false);
    }
  };

  const renderBarChart = (stacked: boolean) => (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11 }}
          angle={data.labels.length > 6 ? -45 : 0}
          textAnchor={data.labels.length > 6 ? 'end' : 'middle'}
          height={data.labels.length > 6 ? 80 : 30}
        />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e5e5' }}
          formatter={(value: any) => typeof value === 'number' ? value.toLocaleString() : value}
        />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {visibleDatasets.map((ds, i) => (
          <Bar
            key={ds.label}
            dataKey={ds.label}
            fill={COLORS[i % COLORS.length]}
            stackId={stacked ? 'stack' : undefined}
            radius={stacked ? undefined : [4, 4, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11 }}
          angle={data.labels.length > 6 ? -45 : 0}
          textAnchor={data.labels.length > 6 ? 'end' : 'middle'}
          height={data.labels.length > 6 ? 80 : 30}
        />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e5e5' }}
          formatter={(value: any) => typeof value === 'number' ? value.toLocaleString() : value}
        />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {visibleDatasets.map((ds, i) => (
          <Line
            key={ds.label}
            type="monotone"
            dataKey={ds.label}
            stroke={COLORS[i % COLORS.length]}
            strokeWidth={2}
            dot={{ fill: COLORS[i % COLORS.length], r: 3 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => {
    // Pie chart uses first dataset only
    const ds = visibleDatasets[0];
    if (!ds) return null;

    const pieData = data.labels.map((label, i) => ({
      name: label,
      value: ds.values[i] ?? 0,
    })).filter(d => d.value > 0);

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={compact ? 40 : 60}
            outerRadius={compact ? 80 : 110}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
            labelLine={{ stroke: '#999', strokeWidth: 1 }}
          >
            {pieData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e5e5' }}
            formatter={(value: any) => typeof value === 'number' ? value.toLocaleString() : value}
          />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" ref={chartRef}>
      <ChartControls
        chartType={chartType}
        onChartTypeChange={setChartType}
        showLegend={showLegend}
        onToggleLegend={() => setShowLegend(!showLegend)}
        datasets={data.datasets}
        selectedDatasetIndex={selectedDatasetIndex}
        onSelectDataset={setSelectedDatasetIndex}
        onExportPng={handleExportPng}
        onExportCsv={handleExportCsv}
      />
      <div className="px-2 py-3 min-h-[200px]">
        {renderChart()}
      </div>
    </div>
  );
};

export default ChatDataChart;
